import React from 'react';
import Button from '../../../v2BaseComponents/Button';
import { getIntl } from '../../../../utils/intl';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import { useIsSocialAuthDisabled } from '../../../../hooks/useUrlAppParams';
import { useFederatedAuth } from '../../../../hooks/useFederatedAuth';
import { useRootNavigation } from '../../../../utils/appNavigationNames';
import Stack from '../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../utils/spacing';
import Text from '../../../v2BaseComponents/Text';
import { Platform, Image, Pressable } from 'react-native';
import Images from '../../../../Images';
import { openURL } from 'expo-linking';
import { appStoreAppUrl, playStoreAppUrl } from '../../../../utils/constants';

const intl = getIntl('LoginButtons', {
    signUpExtended: 'Create An Account',
    signUpShortened: 'Sign Up',
    login: 'Sign In',
    or: '- or -',
    google: 'Continue with Google',
    apple: 'Continue with Apple',
});

export type LoginButtonsOwnProps = {};

export type LoginButtonsProps = LoginButtonsOwnProps;

const mobileButtonWidth = 125;
const desktopButtonWidth = 250;

const appStoreAspectRatio = 119.664 / 40;
const playStoreAspectRatio = 270 / 80;

const buttonHeight = 42;

const LoginButtons: React.FC<LoginButtonsProps> = () => {
    const isNative = Platform.OS !== 'web';

    const navigator = useRootNavigation();
    const isMobile = useIsMobileLayout();
    const isSocialAuthDisabled = useIsSocialAuthDisabled();
    const federatedAuth = useFederatedAuth();

    const buttonWidth =
        isMobile && !isNative
            ? mobileButtonWidth
            : Platform.OS === 'web'
            ? buttonHeight * playStoreAspectRatio
            : desktopButtonWidth;

    const signupButton = (
        <Button
            title={intl('signUpShortened')}
            onPress={() => {
                navigator.navigate('signup');
            }}
            width={buttonWidth}
            testID="signup-button"
        />
    );

    const loginButton = (
        <Button
            title={intl('login')}
            onPress={() => {
                navigator.navigate('login');
            }}
            testID="login-button"
            width={buttonWidth}
        />
    );

    const appStoreButton = (
        <Pressable
            onPress={() => {
                openURL(appStoreAppUrl);
            }}
            testID="app-store-button"
        >
            <Image
                source={Images.downloadInAppStore}
                style={{
                    height: buttonHeight,
                    width: appStoreAspectRatio * buttonHeight,
                }}
                resizeMode="contain"
            />
        </Pressable>
    );

    const appleButton = (
        <Button
            variant="secondary"
            width={buttonWidth}
            onPress={() => {
                federatedAuth.signInWithRedirect('Apple');
            }}
            socialLogin="apple"
            displayText={!isMobile || isNative}
            testID="apple-login-button"
        />
    );

    const playStoreButton = (
        <Pressable
            onPress={() => {
                openURL(playStoreAppUrl);
            }}
            testID="play-store-button"
        >
            <Image
                source={Images.downloadInGooglePlay}
                style={{
                    height: buttonHeight,
                    width: playStoreAspectRatio * buttonHeight,
                }}
                resizeMode="contain"
            />
        </Pressable>
    );

    const googleButton = (
        <Button
            variant="secondary"
            onPress={() => {
                federatedAuth.signInWithRedirect('Google');
            }}
            socialLogin="google"
            width={buttonWidth}
            displayText={!isMobile || isNative}
            testID="google-login-button"
        />
    );

    if (isNative) {
        return (
            <Stack vertical space={getSpacing(3)} style={{ alignSelf: 'stretch' }} align="center">
                {signupButton}
                {loginButton}
                <Text
                    variant="placeholder-text"
                    style={{ alignSelf: 'center', paddingHorizontal: getSpacing(4) }}
                    alignText="center"
                >
                    {intl('or')}
                </Text>
                {appleButton}
                {googleButton}
            </Stack>
        );
    } else {
        if (isSocialAuthDisabled) {
            return (
                <>
                    <Stack
                        vertical
                        space={getSpacing(3)}
                        style={{
                            justifyContent: isMobile ? 'space-around' : 'flex-start',
                        }}
                    >
                        {signupButton}
                    </Stack>
                    <Text
                        variant="placeholder-text"
                        style={{ alignSelf: 'center', paddingHorizontal: getSpacing(4) }}
                        alignText="center"
                    >
                        {intl('or')}
                    </Text>
                    <Stack
                        space={getSpacing(3)}
                        vertical
                        style={{
                            justifyContent: isMobile ? 'space-around' : 'flex-start',
                        }}
                    >
                        {loginButton}
                    </Stack>
                </>
            );
        } else {
            return (
                <>
                    <Stack
                        vertical
                        space={getSpacing(3)}
                        style={{
                            justifyContent: isMobile ? 'space-around' : 'flex-start',
                        }}
                    >
                        {signupButton}
                        {loginButton}
                    </Stack>
                    <Text
                        variant="placeholder-text"
                        style={{ alignSelf: 'center', paddingHorizontal: getSpacing(4) }}
                        alignText="center"
                    >
                        {intl('or')}
                    </Text>
                    <Stack
                        space={getSpacing(3)}
                        vertical
                        style={{
                            justifyContent: isMobile ? 'space-around' : 'flex-start',
                        }}
                    >
                        {Platform.OS === 'web' ? appStoreButton : appleButton}
                        {Platform.OS === 'web' ? playStoreButton : googleButton}
                    </Stack>
                </>
            );
        }
    }
};

export default React.memo(LoginButtons);
